import { initAndLoadAmr } from 'Cloud/Application/Editor/MyOffice/amr/amr.helpers';
import { initAndLoadWopi } from 'Cloud/Application/Editor/MyOffice/wopi/wopi.helpers';
import { logger } from 'lib/logger';
import xray from 'lib/xray';
import { wopiFallbackConfig, wopiJsErrorsFallbackList, wopiTimeoutFallbackTimeSec } from 'reactApp/appHelpers/featuresHelpers';

import AmrApi from './amrApi.js';
import { createIframe } from './helpers/createIframe';
import { MY_OFFICE_FORMAT, MY_OFFICE_FRAME_ID, MY_OFFICE_IFRAME } from './myOffice.constants';
import {
    EMyOfficeEvent,
    EWopiExtraEventRadarType,
    IInitMyoffice,
    IMyOfficeError,
    IMyOfficeReady,
    isCustomErrorMessage,
    isCustomServiceErrorMessage,
    isMyOfficeEvent,
    MYOFFICE_VARIANTS,
    PostMessageType,
} from './myOffice.types';

let editorApi;
let fileInfo;

/**
 * В postMessage от Моего офиса приходит js-ошибка (полностью весь текст)
 * В кодах ошибок лежит часть этого текста, по которому можно идентифицировать ту или иную ошибку
 */
const ErrorsCodes = {
    network: 'Network Error',
    rowIndex: 'rowIndex',
    localStorage: 'localStorage',
    code400: 'code 400',
    code500: 'code 500',
    eUndefined: 'e is undefined',
};

const doAmrFallback = ({
    iframeWopi,
    iframeAmr,
    isMyOfficeForMail,
    amrParams,
    listeners,
    documentMode,
    container,
    type,
    isEditAttaches,
}: IInitMyoffice) => {
    // удаляем iframe с wopi, так как в нем произошла ошибка
    iframeWopi?.parentElement?.removeChild(iframeWopi);

    if (!iframeAmr) {
        return;
    }

    const newIframe = createIframe({
        url: MY_OFFICE_IFRAME,
        id: iframeAmr?.id || '',
        container,
        iframe: iframeAmr,
    });

    initAndLoadAmr({
        iframe: newIframe,
        item: amrParams?.item,
        listeners,
        documentMode,
        isMyOfficeForMail,
        isEditAttaches,
    });

    newIframe.src = MY_OFFICE_IFRAME;
    amrParams?.initShowAmrIframe();

    xray.send('amr_fallback', {
        i: type,
    });
};

const needToDoFallback = (
    data: PostMessageType
): {
    doFallback: boolean;
    error?: string;
} => {
    const { enableFallback, enableFallbackServiceError, enableFallbackPostMsgError, hostReg } = wopiFallbackConfig;

    const wopiRegexp = new RegExp(hostReg);
    const isOriginOk = origin === window.location.origin || origin.match(wopiRegexp);

    if (!isOriginOk || typeof data !== 'object') {
        return { doFallback: false };
    }

    // Фолбек на случай, если сервис Моего офиса лежит
    if (enableFallbackServiceError && isCustomServiceErrorMessage(data)) {
        return { doFallback: true, error: data.type };
    }

    // Фолбек на случай 'error' в post message от Моего офиса
    if (enableFallbackPostMsgError && isMyOfficeEvent<IMyOfficeError>(data, EMyOfficeEvent.error)) {
        xray.send('wopi_pm_error', {
            rlog: 'wopi_pm_error',
            rlog_message: data?.argumentList[1]?.value,
        });
        return { doFallback: true, error: 'wopi_pm_error' };
    }

    // Фолбек на случай js-ошибки Моего офиса
    if (enableFallback && isCustomErrorMessage(data)) {
        const criticalError = wopiJsErrorsFallbackList?.find((errorCode: string) => data.error.message.includes(ErrorsCodes[errorCode]));

        if (criticalError) {
            return { doFallback: true, error: criticalError };
        }
    }

    return { doFallback: false };
};

const listenToWopiMessages = (params: IInitMyoffice): void => {
    let isLoad = false;
    setTimeout(() => {
        // Фолбек на случай, если WOPI не загрузилось за заданное в фиче wopiTimeoutFallbackTimeSec время
        if (wopiFallbackConfig?.enableTimeoutFallback && !isLoad) {
            doAmrFallback({
                ...params,
                type: 'timeout',
            });
        }
    }, wopiTimeoutFallbackTimeSec * 1000);

    window.addEventListener('message', ({ data, origin }: MessageEvent<PostMessageType>) => {
        if (!origin || !wopiFallbackConfig?.hostReg) {
            return;
        }

        const { doFallback, error } = needToDoFallback(data);

        if (doFallback) {
            doAmrFallback({ type: error, ...params });
        } else if (isMyOfficeEvent(data)) {
            let extraEventType: EWopiExtraEventRadarType | null = null;
            const eventType = data.argumentList[0].value;
            const eventData = data.argumentList[1]?.value;

            /**
             * @interface IMyOfficeReady
             * Смотреть интерфейс события ready, там может на ready возникнуть ошибка
             */
            if (isMyOfficeEvent<IMyOfficeReady>(data, EMyOfficeEvent.ready)) {
                isLoad = true;

                if (data.argumentList[1]?.value.isError) {
                    extraEventType = EWopiExtraEventRadarType.error;
                }
            }

            xray.send(`wopi_2_2_post_msg`, {
                i: `${eventType}${extraEventType ? `-${extraEventType}` : ''}`,
                rlog: 'cloud_wopi_postmsgs',
                rlog_message: eventData,
            });
        }
    });
};

export const initAndLoadMyOffice = ({
    container,
    iframeAmr,
    iframeWopi,
    isMyOfficeForMail,
    wopiParams,
    amrParams,
    myOfficeVariant,
    listeners,
    documentMode = 'view',
    isEditAttaches,
}: IInitMyoffice): undefined | (() => Promise<void>) | (() => void) => {
    const isWopiEnabled = myOfficeVariant === MYOFFICE_VARIANTS.wopi;
    const iframeId = (isWopiEnabled ? iframeWopi?.id : iframeAmr?.id) || MY_OFFICE_FRAME_ID;
    const inputFrame = isWopiEnabled ? iframeWopi : iframeAmr;

    if (!inputFrame) {
        return;
    }

    const iframe = createIframe({
        url: isWopiEnabled ? '' : MY_OFFICE_IFRAME,
        id: iframeId,
        container,
        iframe: inputFrame,
        extraData: {
            'wopi-enabled': String(isWopiEnabled),
        },
    });

    iframe.addEventListener('load', () => {
        logger.warn(`МойОфис. ${isWopiEnabled ? 'Серверная версия (WOPI)' : 'Клиентская версия (АМР)'}`);
    });

    const isWopiDataOk = wopiParams?.url && wopiParams?.accessParams?.access_token_ttl && wopiParams?.accessParams?.access_token;

    if (isWopiEnabled && isWopiDataOk) {
        listenToWopiMessages({
            iframeWopi,
            iframeAmr,
            amrParams,
            listeners,
            documentMode,
            isMyOfficeForMail,
            container,
            isEditAttaches,
        });

        return initAndLoadWopi({
            container,
            iframeWopi: iframe,
            iframeId,
            listeners,
            isMyOfficeForMail,
            wopiParams,
            isEditAttaches,
        });
    }

    return initAndLoadAmr({
        iframe,
        item: amrParams?.item,
        listeners,
        documentMode,
        isMyOfficeForMail,
        isEditAttaches,
    });
};

export async function actionDownloadDocument({ filename, format }) {
    if (!editorApi || !fileInfo) {
        return;
    }
    logger.info('Сохранение', { format, filename, ...fileInfo });
    const content = await editorApi.saveDocument(format);
    const blob = new Blob([content]);
    const link = document.createElement('a');
    // Browsers that support HTML5 download attribute
    if (link.download !== undefined) {
        let name = filename;
        const currentExt = name.slice(name.lastIndexOf('.') + 1);
        const ext = AmrApi.getDocumentExtension(fileInfo.type, format, currentExt);
        const url = URL.createObjectURL(blob);

        if (ext && currentExt !== ext) {
            name = `${name.slice(0, Math.max(0, name.lastIndexOf('.')))}.${ext}`;
        }

        link.setAttribute('href', url);
        link.setAttribute('download', name);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url));
    }
}

export const getDownloadDropdownList = (filename: string, ext: string) => {
    const onDropdownDownload = (format) => {
        actionDownloadDocument({ filename, format });
    };

    const format = AmrApi.getOptions(true)?.EXTENSION_TO_TYPE[ext.toLowerCase()]?.format;

    const oxmlText = ((format === MY_OFFICE_FORMAT.oxml && ext) || 'oxml').toUpperCase();
    const odfText = ((format === MY_OFFICE_FORMAT.odf && ext) || 'odf').toUpperCase();

    return [
        { id: MY_OFFICE_FORMAT.oxml, text: oxmlText, onClick: onDropdownDownload },
        { id: MY_OFFICE_FORMAT.odf, text: odfText, onClick: onDropdownDownload },
        { id: MY_OFFICE_FORMAT.pdf, text: 'PDF', onClick: onDropdownDownload },
        { id: MY_OFFICE_FORMAT.pdfa, text: 'PDF/A', onClick: onDropdownDownload },
    ];
};
